import * as PIXI from 'pixi.js';
import { MaxRectsPacker, Bin } from 'maxrects-packer';

const vert = `
in vec2 aPosition;
out vec2 vTextureCoord;
out vec2 vNormCoord;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

vec4 filterVertexPosition( void )
{
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;

    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0*uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;

    return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord( void )
{
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void)
{
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
    vNormCoord = aPosition.xy;
}
`;
const fragment = `
in vec2 vTextureCoord;
in vec2 vNormCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uMask;
uniform sampler2D uDecal;

void main(void) {
    float mask = texture(uMask, vNormCoord).r;
    vec4 decal = texture(uDecal, vNormCoord) * mask;
    vec4 base = texture(uTexture, vTextureCoord) * mask;

    finalColor = decal +  (1.0 - decal.a) * base;
}
`;

class CompositeFilter extends PIXI.Filter {
    constructor(mask, decal) {
        super({
            glProgram: PIXI.GlProgram.from({vertex: vert, fragment, name: 'decal-composite-filter'}),
            resources: {
                uMask: mask.source,
                uDecal: decal.source,
            },
        });
    }
}

export class Decal {
    /**
     *
     * @param {PIXI.RenderTexture} maskRT
     * @param {PIXI.RenderTexture} decalRT
     * @param {string} key
     */
    constructor(maskRT, decalRT, key) {
        this.maskRT = maskRT;
        this.decalRT = decalRT;
        this.key = key;
        this.id = key;
        this.owner = null;
        /**
         *
         * @type {PIXI.Sprite}
         */
        this.sprite = null;
    }

    destroy() {
        this.maskRT = null;
        this.decalRT = null;
        this.owner = null;
    }
}

export class DecalLayer {
    /**
     *
     * @param {Bin<{x: number, y: number, width: number, height: number, texture: PIXI.Texture, key: string}>} bin
     * @param {PIXI.Application} app
     * @param {PackedDecalSystem} system
     */
    constructor(bin, app, system) {
        this._bin = bin;
        this.app = app;
        this.system = system;
        /**
         *
         * @type {PIXI.Container}
         * @private
         */
        this._sourceContainer = null;
        /**
         *
         * @type {PIXI.Container}
         * @private
         */
        this._particleContainer = null;
        /**
         *
         * @type {PIXI.Container}
         * @private
         */
        this._combain = null;
        /**
         *
         * @type {PIXI.RenderTexture}
         * @private
         */
        this._maskRT = null;
        /**
         *
         * @type {PIXI.RenderTexture}
         * @private
         */
        this._decalRT = null;
        /**
         *
         * @type {PIXI.RenderTexture}
         * @private
         */
        this._resultRT = null;
        /**
         *
         * @type {Decal[]}
         */
        this.decals = [];
    }

    init() {
        this._sourceContainer = new PIXI.Container();
        this._particleContainer = new PIXI.Container();
        this._combain = new PIXI.Container();

        const dim = {
            width: this._bin.width,
            height: this._bin.height
        };

        this._decalRT = PIXI.RenderTexture.create(dim);
        this._maskRT = PIXI.RenderTexture.create(dim);
        this._resultRT = PIXI.RenderTexture.create(dim);

        // The composited result is produced entirely by the CompositeFilter on
        // _sourceContainer (set below): it samples _maskRT and _decalRT in-shader and
        // outputs base + decals + carve-holes in one pass. The old path ALSO drew a
        // second, native-alpha-masked decal sprite here (decalSprite.mask = mask),
        // which was redundant with that filter AND was the crash source: rendering
        // _combain into a RenderTexture routed the sprite mask through Pixi's
        // AlphaMaskPipe, whose pooled MaskFilter hit a torn-down bind group
        // (BindGroup.resources === null) during gore. Dropping the native mask
        // removes the AlphaMaskPipe path without changing the composite.
        for(let rect of this._bin.rects) {
            const s = new PIXI.Sprite(rect.texture);

            s.x = rect.x;
            s.y = rect.y;
            s.label = rect.key;

            this._sourceContainer.addChild(s);
        }

        this._combain.addChild(this._sourceContainer);

        this.fill();

        this._sourceContainer.filters = [
            new CompositeFilter(
                this._maskRT,
                this._decalRT
            )
        ];
    }

    fill() {

        // render decal base texture
        this.app.renderer.render({container: this._sourceContainer, target: this._resultRT, clear: true});

        // mask is full white with black background
        const filter = new PIXI.ColorMatrixFilter();
        filter.matrix = [
            0, 0, 0, 1, 0,
            0, 0, 0, 1, 0,
            0, 0, 0, 1, 0,
            0, 0, 0, 0, 1
        ];

        this._sourceContainer.filters = [filter];
        try {
            this.app.renderer.render({container: this._sourceContainer, target: this._maskRT, clear: false});
        } finally {
            this._sourceContainer.filters = null;
            filter.destroy();
        }

    }

    /**
     *
     * @param {string} key
     */
    getCachedDecalEntry(key) {
        return this.decals.find(e => e.key === key);
    }
    /**
     *
     * @param {string} key
     * @returns {Decal}
     */
    getDecalEntry(key) {
        const cache = this.getCachedDecalEntry(key);
        if (cache) {
            return  cache;
        }

        const frame = this._bin.rects.find(e => e.key === key);

        if(!frame) {
            return  null;
        }

        const decalTexture = new PIXI.Texture({
            source: this._resultRT.source,
            frame: new PIXI.Rectangle(frame.x,frame.y, frame.width,frame.height)
        });

        const maskTexture = new PIXI.Texture({
            source: this._maskRT.source,
            frame: new PIXI.Rectangle(frame.x,frame.y, frame.width,frame.height)
        });

        const decal = new Decal(maskTexture, decalTexture, key);

        decal.sprite = this._sourceContainer.children.find(e => e.label === key);
        decal.owner = this.system;

        this.decals.push(decal);

        return decal;
    }

    /**
     *
     * @param {Array<PIXI.Sprite & {carving: boolean, carvingSize: number, taskId: number}>} tasks
     */
    flushDecalTasks (tasks) {

        const parts = this._particleContainer;
        const carversTask = tasks.filter((e) => e.carving);

        parts.addChild(...tasks);

        this.app.renderer.render({
            container: parts,
            target: this._decalRT,
            clear: false
        });

        if (carversTask.length > 0) {
            parts.removeChildren();
            parts.addChild(...carversTask);

            const originalState = [];
            for(const c of carversTask) {
                originalState.push(c.scale.x, c.scale.y, c.tint);
                c.scale.set(c.carvingSize || c.scale.x * 0.6);
                c.tint = 0x0;
            }

            try {
                this.app.renderer.render({
                    container: parts,
                    target: this._maskRT,
                    clear: false
                });
            } finally {
                // The same batch is rendered into every packed atlas layer.
                // Restore its visible decal transform before the next layer.
                for(let i = 0; i < carversTask.length; i++) {
                    const c = carversTask[i];
                    c.scale.set(originalState[i * 3], originalState[i * 3 + 1]);
                    c.tint = originalState[i * 3 + 2];
                }
            }

        }

        parts.removeChildren();

        this.flushDecalTexture();
    }

    /**
     * Render to itself to apply mask to top - add holes
     */
    flushDecalTexture() {
        this.app.renderer.render({
            container: this._combain,
            target: this._resultRT,
            clear: true
        });
    }

    destroy() {
        // CompositeFilter owns bind groups that listen to the mask/decal texture
        // sources. Detach and destroy those filters before destroying the render
        // textures, otherwise Pixi warns that a textureSource is still shader-bound.
        const filters = this._sourceContainer?.filters || [];
        if (this._sourceContainer) this._sourceContainer.filters = null;
        filters.forEach(filter => filter.destroy());

        // Destroy display objects while their textures are still valid. The source
        // sprites use shared asset textures, so explicitly leave those textures alone.
        this._combain && this._combain.destroy({
            children: true,
            texture: false,
            textureSource: false
        });
        this._particleContainer && this._particleContainer.destroy({
            children: true,
            texture: false,
            textureSource: false
        });

        this._maskRT && this._maskRT.destroy(true);
        this._decalRT && this._decalRT.destroy(true);
        this._resultRT && this._resultRT.destroy(true);

        this._bin = null;
        this._sourceContainer = null;
        this._particleContainer = null;
        this._maskRT = null;
        this._decalRT = null;
        this._resultRT = null;
        this._combain = null;
    }
}

export class PackedDecalSystem {
    static ATLAS_MAX_SIZE = 1024;
    static TASK_POOL_SIZE = 32;
    /**
     *
     * @param {PIXI.Application} app
     * @param {string} key
     */
    constructor(app, key) {
        this.app = app;
        this.key = key;
        /**
         *
         * @type {DecalLayer[]}
         */
        this.layers = [];
        this.tasks = [];
        this.taskPool = [];

        this.frameId = 0;
        this.lastFrameId = 0;
        this.frameTrottle = 1;
    }

    /**
     * Get EXIST decal for key
     * @param {string} key
     * @return {Decal|null}
     */
    getDecalFor(key) {
        for(const layer of this.layers) {
            const decal = layer.getDecalEntry(key);
            if (decal)
                return decal;
        }

        return  null;
    }
    /**
     *
     * @param {PIXI.Texture[]} tex
     * @returns {DecalLayer[]}
     */
    generateLayerForGroup(tex) {
        const pack = new MaxRectsPacker(
            PackedDecalSystem.ATLAS_MAX_SIZE,
            PackedDecalSystem.ATLAS_MAX_SIZE,
            2, {
                pot: false
            }
        );

        const layers = this.layers;

        pack.addArray(tex.map(e => ({
                width: e.width,
                height: e.height,
                key: e.key || e.label,
                texture: e
        })));

        pack.next();
        pack.bins.forEach(bin => {
            const layer = new DecalLayer(bin, this.app, this);
            layer.init();
            layers.push(layer);
        });

        return layers;
    }

    /**
     *
     * @param {PIXI.Sprite & {carving: boolean, carvingSize: number, taskId: number}} task
     */
    pushDecalUpdateTask (task) {
        task.taskId = this.frameId;
        this.tasks.push(task);
    }

    acquireDecalTask(texture) {
        const task = this.taskPool.pop() || new PIXI.Sprite(texture);
        task.texture = texture;
        task.visible = true;
        return task;
    }

    releaseDecalTasks(tasks) {
        for(const task of tasks) {
            if(task.parent) task.parent.removeChild(task);
            task.position.set(0, 0);
            task.rotation = 0;
            task.scale.set(1);
            task.tint = 0xFFFFFF;
            task.carving = false;
            task.carvingSize = 0;
            delete task.taskId;

            if(this.taskPool.length < PackedDecalSystem.TASK_POOL_SIZE) {
                this.taskPool.push(task);
            } else {
                task.destroy({texture: false, textureSource: false});
            }
        }
    }

    flushDecalTasks () {
        this.frameId ++;

        if (this.tasks.length  === 0 || (this.frameId - this.lastFrameId) < this.frameTrottle) {
            return;
        }

        this.lastFrameId = this.frameId;


        const tasks = this.tasks.splice(0, this.tasks.length);
        try {
            for(let layer of this.layers) {
                layer.flushDecalTasks(tasks);
            }
        } finally {
            this.releaseDecalTasks(tasks);
        }
    }

    flushDecalTexture() {
        if (this.tasks.length > 0) {
            this.flushDecalTasks();
            return;
        }

        for(let layer of this.layers) {
            layer.flushDecalTexture();
        }
    }

    destroy() {
        const pooledTasks = this.tasks.concat(this.taskPool);
        for(const task of pooledTasks) {
            if(task.parent) task.parent.removeChild(task);
            task.destroy({texture: false, textureSource: false});
        }
        this.tasks.length = 0;
        this.taskPool.length = 0;
        this.layers && this.layers.forEach(e => e.destroy());
        this.layers = null;
    }
}

/**
 * @type {Map<string, PackedDecalSystem>}
 */
const _decalsSystems = new Map();

/**
 *
 * @param {string} key
 * @returns {PackedDecalSystem | null}
 */
export function getDecalSystem(key) {
    return _decalsSystems.get(key);
}

export function setDecalSystem(key, sys) {
    _decalsSystems.set(key, sys);
}

export function getAllSystems() {
    return _decalsSystems;
}

export function dropSystems() {
    for(let s of _decalsSystems.values()) {
        s.destroy();
    }

    _decalsSystems.clear();
}
